import React from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

// Shared "research journal" paper kit — the print-ready, serif, letter-portrait
// look plus the Journal/Documentation view toggle, reflection block, researcher
// attestation, and ISEF AI disclosure. Extracted from the Step 1 journal so all
// three steps render the same artifact. See step1/step2/step3 JournalTab.

export type JournalView = 'summary' | 'full' | 'audit'

export interface StepReflection {
  step_number:     number
  reflection:      string
  acknowledgments: string | null
  ai_use_note:     string | null
  written_at:      string
  updated_at:      string
}

export interface JournalNote {
  note_id:      string
  step_number:  number
  source_label: string | null
  content:      string
  saved_at:     string
}

// ── Date helpers ────────────────────────────────────────────────────────────────

export function fmtDate(ts: string) {
  return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}
export function fmtDateTime(ts: string) {
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

// ── Print helpers (in-page print, isolates the target element) ───────────────────

function injectPrintCSS(targetId: string) {
  const id  = 'ardent-journal-print-css'
  let style = document.getElementById(id) as HTMLStyleElement | null
  if (!style) {
    style    = document.createElement('style')
    style.id = id
    document.head.appendChild(style)
  }
  style.textContent = `
    @media print {
      @page { margin: 0.75in; size: letter portrait; }
      body > * { visibility: hidden !important; }
      #${targetId}, #${targetId} * { visibility: visible !important; }
      #${targetId} {
        position: fixed; top: 0; left: 0;
        width: 100%; height: auto;
        background: #fff !important;
        box-shadow: none !important;
        border: none !important;
      }
      .journal-no-break { page-break-inside: avoid; }
      .journal-page-break { page-break-before: always; }
    }
  `
}
function clearPrintCSS() {
  const el = document.getElementById('ardent-journal-print-css')
  if (el) el.textContent = ''
}
export function handlePrint(targetId: string) {
  injectPrintCSS(targetId)
  window.print()
  clearPrintCSS()
}

// ── Paper-style typography primitives ───────────────────────────────────────────

export function Jt({ label, value }: { label: string; value: string }) {
  return (
    <Typography sx={{ fontFamily: 'inherit', fontSize: '10pt' }}>
      <Box component="span" sx={{ fontWeight: 'bold' }}>{label}:&nbsp;</Box>
      {value}
    </Typography>
  )
}

export const J = {
  Page: ({ id, children }: { id: string; children: React.ReactNode }) => (
    <Box id={id} sx={{
      fontFamily: '"Georgia", "Times New Roman", Times, serif',
      fontSize: '11pt', lineHeight: 1.65, color: '#111',
      backgroundColor: '#fff',
      p: { xs: 2, sm: '0.65in' },
      maxWidth: '7.5in', mx: 'auto',
      border: '1px solid #ccc',
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    }}>
      {children}
    </Box>
  ),
  Header: ({ title, subtitle, date }: { title: string; subtitle?: string; date: string }) => (
    <Box sx={{ mb: 3, pb: 1.5, borderBottom: '2px solid #111' }}>
      <Typography sx={{ textAlign: 'center', fontFamily: 'inherit', fontWeight: 'bold', fontSize: '14pt', letterSpacing: '0.12em', mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ textAlign: 'center', fontFamily: 'inherit', fontSize: '10pt', color: '#444', mb: 1.5 }}>
        {subtitle ?? 'Ardent Scholar'}
      </Typography>
      <Box sx={{ borderTop: '1px solid #111', borderBottom: '1px solid #111', py: 0.75 }}>
        <Jt label="Date" value={date} />
      </Box>
    </Box>
  ),
  Section: ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ mt: 2.5, mb: 1 }}>
      <Typography sx={{ fontFamily: 'inherit', fontWeight: 'bold', fontSize: '10pt', letterSpacing: '0.08em', mb: 0.25 }}>
        {children}
      </Typography>
      <Box sx={{ borderBottom: '1px solid #888' }} />
    </Box>
  ),
  Body: ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
    <Typography sx={{ fontFamily: 'inherit', fontSize: '11pt', mb: 1, ...sx }}>
      {children}
    </Typography>
  ),
  Label: ({ children }: { children: React.ReactNode }) => (
    <Typography sx={{ fontFamily: 'inherit', fontSize: '9pt', fontWeight: 'bold', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
      {children}
    </Typography>
  ),
  Quote: ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ borderLeft: '3px solid #999', pl: 1.5, my: 1 }}>
      <Typography sx={{ fontFamily: 'inherit', fontSize: '11pt', fontStyle: 'italic', color: '#222', whiteSpace: 'pre-wrap' }}>
        {children}
      </Typography>
    </Box>
  ),
  Signatures: () => (
    <Box className="journal-no-break" sx={{ mt: 4, pt: 2, borderTop: '1px solid #888' }}>
      <Typography sx={{ fontFamily: 'inherit', fontWeight: 'bold', fontSize: '10pt', letterSpacing: '0.08em', mb: 0.5 }}>
        RESEARCHER ATTESTATION
      </Typography>
      <Typography sx={{ fontFamily: 'inherit', fontSize: '10pt', fontStyle: 'italic', mb: 2.5 }}>
        I certify that this journal reflects my independent research decisions and reasoning. I used
        AI assistance and accepted help from mentors and others as documented; the decisions and
        their reasoning are my own.
      </Typography>
      {[
        'Researcher Signature',
        'Adult Sponsor / Mentor Signature',
      ].map(label => (
        <Box key={label} sx={{ display: 'grid', gridTemplateColumns: '2.5fr 1fr', gap: 3, mb: 2 }}>
          <Box>
            <Box sx={{ borderBottom: '1px solid #333', height: 28, mb: 0.25 }} />
            <Typography sx={{ fontFamily: 'inherit', fontSize: '9pt', color: '#555' }}>{label}</Typography>
          </Box>
          <Box>
            <Box sx={{ borderBottom: '1px solid #333', height: 28, mb: 0.25 }} />
            <Typography sx={{ fontFamily: 'inherit', fontSize: '9pt', color: '#555' }}>Date</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  ),
  // ISEF-flavored AI disclosure — Documentation view. Pass `labels` to render
  // friendly names for the AI function keys.
  Disclosure: ({ functions, date, full, labels }: {
    functions: string[]; date: string; full?: boolean; labels?: Record<string, string>
  }) => (
    <Box className="journal-no-break" sx={{ mt: 3, pt: 1.5, borderTop: '1px solid #888' }}>
      <Typography sx={{ fontFamily: 'inherit', fontWeight: 'bold', fontSize: '10pt', letterSpacing: '0.08em', mb: 0.5 }}>
        AI ASSISTANCE DISCLOSURE
      </Typography>
      <Typography sx={{ fontFamily: 'inherit', fontSize: '9.5pt', mb: 0.75 }}>
        In compliance with ISEF AI Usage Policy (Rule 4.2): AI assistance was used in the following
        capacities during this work. All research decisions were made independently by the student.
      </Typography>
      {full && functions.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0.75 }}>
          {functions.map(f => (
            <Typography key={f} component="li" sx={{ fontFamily: 'inherit', fontSize: '9.5pt', mb: 0.25 }}>
              {labels?.[f] ?? f}
            </Typography>
          ))}
        </Box>
      )}
      <Typography sx={{ fontFamily: 'inherit', fontSize: '9.5pt', color: '#444' }}>
        AI Systems: Claude Sonnet 4.6, Claude Haiku 4.5 | Anthropic PBC · {date}
      </Typography>
    </Box>
  ),
}

// ── Reflection + saved-notes block ──────────────────────────────────────────────

export function ReflectionBlock({ refl, notes }: { refl: StepReflection | null; notes: JournalNote[] }) {
  if (!refl && notes.length === 0) return null
  return (
    <>
      {refl && (
        <Box sx={{ mt: 2, mb: 1.5 }}>
          <J.Section>WHAT I LEARNED AT THIS STEP</J.Section>
          <J.Body sx={{ whiteSpace: 'pre-wrap' }}>{refl.reflection}</J.Body>
          {refl.acknowledgments && (
            <>
              <J.Label>Acknowledgments</J.Label>
              <J.Body sx={{ whiteSpace: 'pre-wrap' }}>{refl.acknowledgments}</J.Body>
            </>
          )}
        </Box>
      )}
      {notes.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <J.Section>SAVED FROM MY AI EXCHANGES</J.Section>
          {notes.map(n => (
            <Box key={n.note_id} className="journal-no-break" sx={{ mb: 1.25 }}>
              {n.source_label && <J.Label>{n.source_label}</J.Label>}
              <J.Quote>{n.content}</J.Quote>
            </Box>
          ))}
        </Box>
      )}
    </>
  )
}

// ── AI engagement metrics + disclosure (Documentation view) ─────────────────────

export interface UsageRow { function_name: string; model?: string; input_tokens: number; output_tokens: number }

export function AiEngagement({ usage, labels, date }: {
  usage: UsageRow[]; labels: Record<string, string>; date: string
}) {
  if (usage.length === 0) return null
  const byFn: Record<string, { input: number; output: number }> = {}
  for (const u of usage) {
    if (!byFn[u.function_name]) byFn[u.function_name] = { input: 0, output: 0 }
    byFn[u.function_name].input  += u.input_tokens
    byFn[u.function_name].output += u.output_tokens
  }
  return (
    <>
      <J.Section>AI ENGAGEMENT METRICS</J.Section>
      <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 1 }}>
        Token counts reflect the amount of context exchanged with the AI systems during this step.
      </J.Body>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit', fontSize: '10pt', mb: 1 }}>
        <Box component="thead">
          <Box component="tr" sx={{ borderBottom: '2px solid #333' }}>
            {['AI Function', 'Input Tokens', 'Output Tokens', 'Total'].map(h => (
              <Box component="th" key={h} sx={{ textAlign: h === 'AI Function' ? 'left' : 'right', pb: 0.5, pr: 1, fontWeight: 'bold' }}>{h}</Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {Object.entries(byFn).map(([fn, c]) => (
            <Box component="tr" key={fn} sx={{ borderBottom: '1px solid #eee' }}>
              <Box component="td" sx={{ py: 0.5, pr: 1 }}>{labels[fn] ?? fn}</Box>
              <Box component="td" sx={{ textAlign: 'right', py: 0.5, pr: 1 }}>{c.input.toLocaleString()}</Box>
              <Box component="td" sx={{ textAlign: 'right', py: 0.5, pr: 1 }}>{c.output.toLocaleString()}</Box>
              <Box component="td" sx={{ textAlign: 'right', py: 0.5, fontWeight: 'bold' }}>{(c.input + c.output).toLocaleString()}</Box>
            </Box>
          ))}
        </Box>
      </Box>
      <J.Disclosure functions={Object.keys(byFn)} date={date} full labels={labels} />
    </>
  )
}

// ── AI feedback rounds (Documentation view) ─────────────────────────────────────

export interface FeedbackRow {
  field:      string
  evaluation: Record<string, any>
  created_at: string
}

// A dimension is any evaluation entry shaped like { state, note } (e.g. shape,
// anchor). Non-dimension keys (ai_disclaimer, suggestions, scalars) are skipped.
function dimensionsOf(ev: Record<string, any>): [string, { state?: string; note: string }][] {
  return Object.entries(ev).filter(
    ([, v]) => v && typeof v === 'object' && !Array.isArray(v) && typeof v.note === 'string',
  ) as [string, { state?: string; note: string }][]
}

export function FeedbackRounds({ rows, fieldLabels }: {
  rows: FeedbackRow[]; fieldLabels: Record<string, string>
}) {
  if (rows.length === 0) return null
  const byField: Record<string, FeedbackRow[]> = {}
  for (const r of rows) (byField[r.field] ??= []).push(r)
  return (
    <>
      <J.Section>AI FEEDBACK ROUNDS</J.Section>
      <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 1 }}>
        Each time you asked the AI for feedback on a section, here's what it said — a record of how your work evolved.
      </J.Body>
      {Object.entries(byField).map(([field, rounds]) => (
        <Box key={field} className="journal-no-break" sx={{ mb: 1.5 }}>
          <J.Label>{fieldLabels[field] ?? field}</J.Label>
          {rounds.map((r, i) => (
            <Box key={i} sx={{ mb: 0.75 }}>
              <Typography sx={{ fontFamily: 'inherit', fontSize: '8.5pt', color: '#777', mb: 0.25 }}>
                Round {i + 1} · {fmtDateTime(r.created_at)}
              </Typography>
              {dimensionsOf(r.evaluation).map(([dim, d]) => (
                <Typography key={dim} sx={{ fontFamily: 'inherit', fontSize: '10pt', mb: 0.25 }}>
                  <Box component="span" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {dim}{d.state ? ` (${d.state})` : ''}:
                  </Box>{' '}{d.note}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      ))}
    </>
  )
}

// ── Journal / Documentation view toggle ─────────────────────────────────────────

const VIEW_OPTIONS: { value: 'journal' | 'documentation'; label: string; sub: string }[] = [
  { value: 'journal',       label: 'Journal',       sub: 'Curated record for fairs and mentors' },
  { value: 'documentation', label: 'Documentation', sub: 'Complete system record of your work' },
]

export function ViewToggle({ value, onChange }: { value: 'journal' | 'documentation'; onChange: (v: 'journal' | 'documentation') => void }) {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, v) => { if (v) onChange(v) }}
      size="small"
      sx={{ flexWrap: 'wrap', gap: 0.5 }}
    >
      {VIEW_OPTIONS.map(opt => (
        <ToggleButton key={opt.value} value={opt.value} sx={{ flexDirection: 'column', py: 0.75, px: 2, lineHeight: 1.3 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{opt.label}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{opt.sub}</Typography>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}
